// CUBE AI — the fourth product (CP-1361). Shared by the server (seat records, metering, access
// gates) and the web app (Portal Users column, Billing card, AI Usage screens, chat launcher).
//
// A CUBE AI seat is sold per user like CDE/PM/DT, but differs in three ways this file encodes:
//   - it never opens the portal (only a PRODUCT seat does — see `hasProductSeat`),
//   - it has no half/guest tier: Full Seat or No Seat only,
//   - every seat is a unique, numbered record whose allowance stays with the seat, not the person.
import { PORTAL_FULL_SEAT, PORTAL_HALF_SEAT } from "./constants.js";

export const CUBE_AI_PLAN_CODE = "AI";
export const CUBE_AI_PLAN_NAME = "CUBE AI";

/** Plan codes whose seats open the portal — every code but CUBE AI (legacy spellings like `JUST_CDE` included). */
export const isProductPlanCode = (planCode: string): boolean => planCode !== CUBE_AI_PLAN_CODE;

/**
 * Whether a per-plan seat map (`accounts.portal[].seat`, `userDetails.seat`, a grid row's `seat`)
 * holds a PRODUCT seat: a full or half seat on any plan other than CUBE AI. An absent or empty map
 * counts as "no product seat" — this is only consulted on `newSignUpFlow` portals, where every
 * member carries an explicit map.
 */
export function hasProductSeat(seat: unknown): boolean {
  if (seat === null || seat === undefined || typeof seat !== "object") return false;
  return Object.entries(seat as Record<string, unknown>).some(
    ([code, value]) => isProductPlanCode(code) && (value === PORTAL_FULL_SEAT || value === PORTAL_HALF_SEAT),
  );
}

/** Whether the seat map says the user holds a CUBE AI Full Seat. */
export function hasCubeAiSeat(seat: unknown): boolean {
  if (seat === null || seat === undefined || typeof seat !== "object") return false;
  return (seat as Record<string, unknown>)[CUBE_AI_PLAN_CODE] === PORTAL_FULL_SEAT;
}

/** Usage bar colour thresholds (percent of the seat's allowance): blue below, amber from here, red at 100. */
export const AI_USAGE_WARN_PERCENT = 80;

/** The rolling chatbot session window. Starts on the user's first CUBE AI interaction. */
export const AI_SESSION_WINDOW_MS = 5 * 60 * 60 * 1000;

/**
 * Why CUBE AI is refusing a user right now. Carried on `getMyAiUsage` and on chat/MCP refusals so
 * the UI can pick the matching banner; the human text travels alongside as `errorMessage`.
 */
export const AI_ACCESS_REASON = {
  SUBSCRIPTION_NOT_LIVE: "SUBSCRIPTION_NOT_LIVE",
  /** Not an active member of the portal (removed or suspended) — reached mainly by MCP tokens issued earlier. */
  NOT_A_MEMBER: "NOT_A_MEMBER",
  NO_AI_SEAT: "NO_AI_SEAT",
  /** Holds a seat numbered above the purchased count (quantity was reduced); blocked until an admin frees a seat or buys more. */
  SEAT_OVER_QUOTA: "SEAT_OVER_QUOTA",
  NO_PRODUCT_SEAT: "NO_PRODUCT_SEAT",
  CYCLE_LIMIT: "CYCLE_LIMIT",
  SESSION_LIMIT: "SESSION_LIMIT",
} as const;

export type AiAccessReason = (typeof AI_ACCESS_REASON)[keyof typeof AI_ACCESS_REASON];

/** "3d 6h" / "2h 10m" / "5m" — the countdown wording the limit banners use. */
export function formatResetDuration(ms: number): string {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60_000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}
